<template>
    <v-row v-if="formData" no-gutters class="px-6 pt-6">
        <Dialog
            v-if="statusDialog.show"
            :value="statusDialog.show"
            :header-text="$t('Project status')"
            header_classes="black white--text"
            persistent
            :width="450"
            @close_modal="closeStatusDialog"
        >
            <v-container v-if="statusDialog.data">
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-text-field
                            v-model="statusDialog.data.name"
                            :error-messages="statusDialog.data.name_error"
                            :label="$t('Status')"
                            outlined
                        />
                    </v-col>
                </v-row>
            </v-container>

            <template v-if="statusDialog.data" slot="footer">
                <v-btn v-if="statusDialog.data.id" text color="error" @click="deleteStatusClick">
                    {{ $t("Delete") }}
                </v-btn>
                <v-spacer/>
                <v-btn text @click="closeStatusDialog">
                    {{ $t("Close") }}
                </v-btn>
                <v-btn class="primary inverted--text" @click="saveStatusClick">
                    {{ $t("OK") }}
                </v-btn>
            </template>
        </Dialog>

        <Dialog
            v-if="progressDialog.show"
            :value="progressDialog.show"
            :header-text="$t('Progress step')"
            header_classes="black white--text"
            persistent
            :width="550"
            @close_modal="closeProgressDialog"
        >
            <v-container v-if="progressDialog.data">
                <v-row class="mb-5">
                    <v-col cols="3" class="pa-1">
                        <ColorField
                            :value="progressDialog.data.color"
                            :error-messages="progressDialog.data.color_error"
                            @change="progressDialog.data.color = $event;"
                        ></ColorField>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="3" class="pa-1">
                        <v-select
                            v-model="progressDialog.data.count_agr"
                            :error-messages="progressDialog.data.count_agr_error"
                            :hide-details="!progressDialog.data.count_agr_error"
                            :items="progressDialog.count_agr_items"
                            item-text="text"
                            item-value="value"
                            outlined
                            offset-y
                            class="mt-0 pt-0"
                            :menu-props="{ contentClass: 'select-dense-items' }"
                        ></v-select>
                    </v-col>
                    <v-col cols="2" class="pa-1">
                        <v-text-field
                            v-model="progressDialog.data.count_f"
                            :error-messages="progressDialog.data.count_error"
                            :hide-details="!progressDialog.data.count_error"
                            class="mt-0 pt-0"
                            outlined
                            @input="numberFieldChanged(progressDialog.data, $event, 'count')"
                        />
                    </v-col>
                    <v-col cols="5" class="pa-1">
                        <v-select
                            v-model="progressDialog.data.count_unit"
                            :error-messages="progressDialog.data.count_unit_error"
                            :hide-details="!progressDialog.data.count_unit_error"
                            :items="progressDialog.count_unit_items"
                            item-text="text"
                            item-value="value"
                            outlined
                            offset-y
                            class="mt-0 pt-0"
                            :menu-props="{ contentClass: 'select-dense-items' }"
                        ></v-select>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="5" class="pa-1">
                        <v-select
                            v-model="progressDialog.data.date_dir"
                            :error-messages="progressDialog.data.date_dir_error"
                            :hide-details="!progressDialog.data.date_dir_error"
                            :items="progressDialog.date_dir_items"
                            item-text="text"
                            item-value="value"
                            outlined
                            offset-y
                            class="mt-0 pt-0"
                            :menu-props="{ contentClass: 'select-dense-items' }"
                        ></v-select>
                    </v-col>
                    <v-col cols="7" class="pa-1">
                        <v-select
                            v-model="progressDialog.data.date_field"
                            :error-messages="progressDialog.data.date_field_error"
                            :hide-details="!progressDialog.data.date_field_error"
                            :items="dateFields"
                            item-text="text"
                            item-value="value"
                            outlined
                            offset-y
                            class="mt-0 pt-0"
                            :menu-props="{ contentClass: 'select-dense-items' }"
                        ></v-select>
                    </v-col>
                </v-row>
            </v-container>

            <template v-if="progressDialog.data" slot="footer">
                <v-btn v-if="progressDialog.data.id" text color="error" @click="deleteProgressClick">
                    {{ $t("Delete") }}
                </v-btn>
                <v-spacer/>
                <v-btn text @click="closeProgressDialog">
                    {{ $t("Close") }}
                </v-btn>
                <v-btn class="primary inverted--text" @click="saveProgressClick">
                    {{ $t("OK") }}
                </v-btn>
            </template>
        </Dialog>

        <v-col cols="12">
            <!-- UKU-3613 clean up needed
            <v-row no-gutters>
                <v-col cols="4" class="pl-3">
                    <v-text-field
                        v-model="formData.name"
                        :label="$t('Rename project')"
                        :error-messages="formData.name_error"
                        outlined
                    ></v-text-field>
                </v-col>
            </v-row>
            -->
            <v-row no-gutters>
                <AppStoreListHeader
                    :item-label="$t('Project statuses')"
                    :add-label="$t('Add')"
                    show-button
                    @add-item="editStatusClick(null)"
                />
                <v-col cols="12" class="px-3 body-2 gray--text">
                    <span v-if="formData.statuses_error" class="local-error">{{ formData.statuses_error }}</span>
                    <span v-else>{{ $t("You can use the statuses to restrict the workflow.") }}</span>
                </v-col>
                <v-col cols="12" class="my-4">
                    <v-data-table
                        :headers="headers.statuses"
                        :items="formData.statuses"
                        disable-pagination
                        :footer-props="{ disablePagination : true, disableItemsPerPage : true }"
                        hide-default-footer
                        hide-default-header
                        class="table-bottom-line table-top-line"
                    >
                        <template v-slot:body="{ items }">
                            <draggable
                                :list="formData.statuses"
                                tag="tbody"
                            >
                                <template v-for="(item, idx) in formData.statuses">
                                    <tr :key="item.id">
                                        <td style="width: 30px; padding-top: 7px; padding-right: 4px;" class="drag-icon">
                                            <v-img
                                                :max-width="10"
                                                class="show-hover"
                                                :src="require('../../../assets/drag-icon.svg')"
                                            />
                                        </td>
                                        <td>{{ item.name }}</td>
                                        <td style="text-align: right;">
                                            <v-btn
                                                v-if="appStoreDialogFlags.canManageAccount()"
                                                icon
                                                class="show-hover"
                                                @click="editStatusClick(item)"
                                            >
                                                <v-icon>mdi-pencil</v-icon>
                                            </v-btn>
                                        </td>
                                    </tr>
                                </template>
                            </draggable>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <AppStoreListHeader
                    :item-label="$t('Project phases')"
                    :add-label="$t('Add')"
                    show-button
                    @add-item="editProgressClick(null)"
                />
                <v-col cols="12" class="px-3 body-2 gray--text">
                    <span v-if="formData.progress_steps_error" class="local-error">
                        {{ formData.progress_steps_error }}
                    </span>
                    <span v-else>{{ $t("Make your progress beautiful.") }}</span>
                </v-col>
                <v-col cols="12" class="my-4">
                    <v-data-table
                        :headers="headers.progress_steps"
                        :items="formData.progress_steps"
                        disable-pagination
                        :footer-props="{ disablePagination : true, disableItemsPerPage : true }"
                        hide-default-footer
                        hide-default-header
                        class="table-bottom-line table-top-line"
                    >
                        <template v-slot:body="{ items }">
                            <draggable
                                :list="formData.progress_steps"
                                tag="tbody"
                            >
                                <template v-for="(item, idx) in formData.progress_steps">
                                    <tr :key="item.id">
                                        <td style="width: 30px; padding-top: 7px; padding-right: 4px;" class="drag-icon">
                                            <v-img
                                                :max-width="10"
                                                class="show-hover"
                                                :src="require('../../../assets/drag-icon.svg')"
                                            />
                                        </td>
                                        <td style="width: 48px;">
                                            <v-avatar
                                                :class="item.color"
                                                size="16"
                                                tile
                                                :style="{ 'borderRadius': '4px' }"
                                            ></v-avatar>
                                        </td>
                                        <td>{{ item.text }}</td>
                                        <td style="text-align: right;">
                                            <v-btn
                                                v-if="appStoreDialogFlags.canManageAccount()"
                                                icon
                                                class="show-hover"
                                                @click="editProgressClick(item)"
                                            >
                                                <v-icon>mdi-pencil</v-icon>
                                            </v-btn>
                                        </td>
                                    </tr>
                                </template>
                            </draggable>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            <v-row v-if="canManageAccount" no-gutters>
                <v-col cols="12" class="subtitle-1 form-subtitle px-3">
                    <span>{{ $t("Permissions") }}</span>
                </v-col>
                <v-col cols="12" class="my-4 pl-3">
                    <v-switch
                        v-model="users_can_manage_wt_templates"
                        :true-value="1"
                        :false-value="0"
                        class="custom-switch"
                        :label="$t('Users can manage workflow templates')"
                        color="primary"
                        hide-details
                    />
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import Vue from "vue";
import {$t} from "@/i18n";
import {ref, computed, watch} from "@vue/composition-api";
import draggable from "vuedraggable";
import {cleanNumber, formatNumber, parseSysDate} from "@/plugins/formatting";
import AppStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import AppStoreListHeader from "../../../components/AppStore/AppStoreListHeader";
import AppModule from "@/store/modules/app";
import AccountModule from "@/store/modules/account";
import Dialog from "../../../components/Dialog";
import {removeFromList, syncItemToList} from "../../../plugins/formatting";
import ColorField from "../../../components/CustomField/ColorField"
import FeatureModule from "@/store/modules/feature";
import {getColorRangeDisp, CountAgrItems, DateDirItems} from "../../../composables/use-task-color";
import errorDialog from "@/views/ErrorDialog/errorDialog"

export default {
    name: "ProjectDialogSettingsTab",
    components: {
        Dialog,
        ColorField,
        draggable,
        AppStoreListHeader
    },
    props: {
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const {appStoreDialogGetters, appStoreDialogFlags, appStoreDialogActions} = AppStoreDialog();
        const {validateFormData, fetchClassifiers, clearValidationErrors, handleErrorResponse, custom_fields, project_types, rights_masks} = AppModule();
        const {execAppAction} = FeatureModule();
        const {putAccountSetup, fetchAccount} = AccountModule();

        const accountFeature = appStoreDialogGetters.getCurrentFeature();
        const accountFeatureSettings = appStoreDialogGetters.getCurrentFeatureSettings();
        const selectedAccount = appStoreDialogGetters.getSelectedCompany();
        const formData = ref(null);
        const users_can_manage_wt_templates = ref(0);
        const headers = ref({
            statuses: [
                {value: "drag", type: "drag"},
                {value: "name", type: "text"},
                {value: "edit", type: "edit"}
            ],
            progress_steps: [
                {value: "drag", type: "drag"},
                {value: "color", type: "color", width: 40},
                {value: "text", type: "text"},
                {value: "edit", type: "edit"}
            ]
        });
        const statusDialog = ref({
            show: false,
            data: null
        });
        const progressDialog = ref({
            show: false,
            data: null,
            count_agr_items: CountAgrItems,
            count_unit_items: [{value: "week", text: $t("Week(s)")}, {value: "day", text: $t("Day(s)")}],
            date_dir_items: DateDirItems,
        });
        const dateFields = computed(() => {
            if (project_types.value[selectedAccount.id] === undefined) return null;
            if (project_types.value[selectedAccount.id].length === 0) return null;
            if (custom_fields.value[selectedAccount.id] === undefined) return null;
            let key = "project.{0}".format(project_types.value[selectedAccount.id][0].id);
            return custom_fields.value[selectedAccount.id]
                .filter(x => x.related_type === key && x.type === "date")
                .map(x => {
                    return {value: x.name, text: x.title};
                });
        });
        const counter = ref(1);
        const formDisabled = computed(() => {
            return accountFeature.status !== "active"
        });
        const loading = ref(false);
        const canManageAccount = computed(() => {
            return (selectedAccount.rights & rights_masks.value.manage_account) === rights_masks.value.manage_account;
        });

        const loadData = () => {
            let reqData = {types: ["custom_fields", "project_types"], account_id: selectedAccount.id};
            fetchClassifiers(reqData)
                .then(() => {
                    let data = {
                        name: $t("Project"),
                        name_error: null,
                        statuses: [],
                        statuses_error: null,
                        progress_steps: [],
                        progress_steps_error: null
                    };
                    if (accountFeatureSettings != null && accountFeatureSettings.data != null) {
                        if (accountFeatureSettings.data.name !== undefined) {
                            data.name = accountFeatureSettings.data.name;
                        }
                        if (accountFeatureSettings.data.statuses !== undefined) {
                            for (let stat of accountFeatureSettings.data.statuses) {
                                data.statuses.push({
                                    id: "tmp{0}".format(counter.value),
                                    code: stat.code,
                                    name: stat.name
                                });
                                counter.value++;
                            }
                        }
                        if (accountFeatureSettings.data.progress_steps !== undefined) {
                            for (let step of accountFeatureSettings.data.progress_steps) {
                                data.progress_steps.push({
                                    id: "tmp{0}".format(counter.value),
                                    color: step.color,
                                    count_agr: step.count_agr,
                                    count: step.count,
                                    count_unit: step.count_unit,
                                    date_dir: step.date_dir,
                                    date_field: step.date_field,
                                    text: getColorRangeDisp(step, dateFields.value),
                                });
                                counter.value++;
                            }
                        }
                    }
                    formData.value = data;
                })
                .catch(ex => errorDialog.showError(ex));

            fetchAccount(selectedAccount.id, {all: 1})
                .then(resp => {
                    users_can_manage_wt_templates.value = resp.data.users_can_manage_wt_templates;
                })
                .catch(ex => errorDialog.showError(ex));
        };
        const saveClick = () => {
            loading.value = true;
            clearValidationErrors(formData.value);

            let data = Vue.clean_and_clone_object(formData.value);

            execAppAction({
                account_id: selectedAccount.id,
                feature_id: accountFeature.feature_id,
                action: "save_project_settings",
                data: data
            })
                .then(resp => {
                    loading.value = false;

                    fetchClassifiers({
                        force: {default: true, types: ["custom_fields", "project_types"]},
                        account_id: selectedAccount.id
                    })
                        .then(() => {
                            appStoreDialogActions.updateCurrentTab(0);
                        })
                        .catch(ex => errorDialog.showError(ex));

                    if (canManageAccount.value) {
                        putAccountSetup({
                            id: selectedAccount.id,
                            append_settings: {users_can_manage_wt_templates: users_can_manage_wt_templates.value}
                        }).catch(ex => errorDialog.showError(ex));
                    }
                })
                .catch(ex => {
                    loading.value = false;
                    handleErrorResponse(formData.value, ex);
                });
        };

        const closeStatusDialog = () => {
            statusDialog.value.show = false;
            statusDialog.value.data = null;
        }
        const editStatusClick = (item) => {
            statusDialog.value.data = Vue.prepare_form_data(
                item == null ? {id: null, name: ""} : Vue.clone_object(item),
                {name: "text"}
            );
            statusDialog.value.show = true;
        }
        const deleteStatusClick = () => {
            removeFromList(formData.value.statuses, statusDialog.value.data.id);
            closeStatusDialog();
        };
        const saveStatusClick = () => {
            if (!validateFormData(statusDialog.value.data, {
                name: {type: "text", required: true}
            })) return;

            if (statusDialog.value.data.id == null) {
                counter.value++;
                statusDialog.value.data.id = "tmp{0}".format(counter.value);
            }
            syncItemToList(formData.value.statuses, statusDialog.value.data);
            closeStatusDialog();
        };

        const closeProgressDialog = () => {
            progressDialog.value.show = false;
            progressDialog.value.data = null;
        };
        const editProgressClick = (item) => {
            progressDialog.value.data = Vue.prepare_form_data(
                item == null ? {
                        id: null,
                        color: "",
                        count_agr: "min",
                        count: 1,
                        count_unit: "week",
                        date_dir: "before",
                        date_field: null
                    }
                    : Vue.clone_object(item),
                {
                    color: "text",
                    count_agr: "choice_single",
                    count: "number",
                    count_unit: "choice_single",
                    date_dir: "choice_single",
                    date_field: "text"
                }
            );
            progressDialog.value.show = true;
        }
        const deleteProgressClick = () => {
            removeFromList(formData.value.progress_steps, progressDialog.value.data.id);
            closeProgressDialog();
        }
        const saveProgressClick = () => {
            if (!validateFormData(progressDialog.value.data, {
                color: {type: "text", required: true},
                count: {type: "integer", required: true},
                count_agr: {type: "choice_single", required: true, choices: ['min', 'max']},
                count_unit: {type: "choice_single", required: true, choices: ['week', 'day']},
                date_dir: {type: "choice_single", required: true, choices: ['before', 'after']},
                date_field: {type: "text", required: true}
            })) return;

            if (progressDialog.value.data.id == null) {
                counter.value++;
                progressDialog.value.data.id = "tmp{0}".format(counter.value);
            }
            progressDialog.value.data.text = getColorRangeDisp(progressDialog.value.data, dateFields.value);
            syncItemToList(formData.value.progress_steps, progressDialog.value.data);
            closeProgressDialog();
        }
        const numberFieldChanged = (form_data, data, field) => {
            let num_str = cleanNumber(data);
            if (isNaN(num_str)) {
                form_data[field + "_error"] = $t("Must be a number");
                form_data[field] = null;
            } else {
                form_data[field + "_error"] = null;
                form_data[field] = parseFloat(num_str);
            }
        };

        loadData();

        watch(() => props.isSelected, () => {
            if (props.isSelected) {
                appStoreDialogActions.setFooterActions([
                    {
                        text: $t("Save"),
                        class: "primary",
                        loading: loading,
                        disabled: formDisabled,
                        click: saveClick
                    }
                ]);
            }
        })

        return {
            formData,
            headers,
            statusDialog,
            progressDialog,
            appStoreDialogFlags,
            dateFields,
            formDisabled,
            loading,
            canManageAccount,
            users_can_manage_wt_templates,
            closeStatusDialog,
            editStatusClick,
            deleteStatusClick,
            saveStatusClick,
            editProgressClick,
            closeProgressDialog,
            deleteProgressClick,
            saveProgressClick,
            numberFieldChanged,
            saveClick
        };
    }
}
</script>
